import React, { useState, useEffect } from 'react';
import { 
  BarChart2, 
  TrendingUp, 
  Calendar, 
  ArrowUpRight, 
  Printer, 
  RefreshCw, 
  FileText, 
  Filter, 
  Eye, 
  AlertTriangle, 
  CheckCircle,
  Truck,
  Layers,
  MapPin,
  Clock,
  User,
  Activity
} from 'lucide-react';
import api from '../utils/api';
import { ReportsResponse, ReportItem, ReportAuditLog, ReportConfigData } from './reports.types';

export default function ReportsView() {
  const [reportsData, setReportsData] = useState<ReportsResponse | null>(null);
  const [filteredOrders, setFilteredOrders] = useState<ReportItem[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<ReportAuditLog[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isSyncing, setIsSyncing] = useState<boolean>(false);
  
  // Guard values for filters applied labels (to render in printed sheet)
  const [appliedFilters, setAppliedFilters] = useState<{
    startDate: string;
    endDate: string;
    urgency: string;
    city: string;
    status: string;
  }>({
    startDate: '',
    endDate: '',
    urgency: 'TODOS',
    city: 'TODAS',
    status: 'TODOS'
  });

  const loadReportsData = async () => {
    setIsLoading(true);
    try {
      const response = await api.get<ReportsResponse>('/reports-data');
      if (response.data) {
        setReportsData(response.data);
        setFilteredOrders(response.data.allOrders || []);
        setFilteredLogs(response.data.auditLogs || []);
      }
    } catch (err) {
      console.error('Erro ao buscar dados do relatório:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadReportsData();
  }, []);

  const handleSyncData = async () => {
    setIsSyncing(true);
    try {
      const response = await api.get<ReportsResponse>('/reports-data');
      if (response.data) {
        setReportsData(response.data);
        setFilteredOrders(response.data.allOrders || []);
        setFilteredLogs(response.data.auditLogs || []);
      }
    } catch (err) {
      console.error('Erro ao sincronizar relatórios:', err);
    } finally {
      setIsSyncing(false);
    }
  };

  // Rule 1: FormData parsing for filtering controls (Strict MVC - NO useState for input capture)
  const handleFilterSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!reportsData) return;

    const formData = new FormData(e.currentTarget);
    const startDate = formData.get('startDate') as string;
    const endDate = formData.get('endDate') as string;
    const urgency = formData.get('urgency') as string;
    const city = formData.get('city') as string;
    const status = formData.get('status') as string;

    // Save filters labels for printed page tracking
    setAppliedFilters({
      startDate,
      endDate,
      urgency,
      city,
      status
    });

    let resultOrders = [...reportsData.allOrders];

    // 1. Data range filter (departure field pt-BR conversion)
    if (startDate) {
      const startObj = new Date(startDate);
      resultOrders = resultOrders.filter(o => {
        const parts = o.departure.split('/');
        if (parts.length === 3) {
          const depObj = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
          return depObj >= startObj;
        }
        return true;
      });
    }

    if (endDate) {
      const endObj = new Date(endDate);
      resultOrders = resultOrders.filter(o => {
        const parts = o.departure.split('/');
        if (parts.length === 3) {
          const depObj = new Date(`${parts[2]}-${parts[1]}-${parts[0]}`);
          return depObj <= endObj;
        }
        return true;
      });
    }

    // 2. Urgency filter
    if (urgency === 'PRIORITY_ONLY') {
      resultOrders = resultOrders.filter(o => o.isPriority && !o.isEmergence);
    } else if (urgency === 'EMERGENCE_ONLY') {
      resultOrders = resultOrders.filter(o => o.isEmergence);
    } else if (urgency === 'CRITICAL_GROUP') {
      resultOrders = resultOrders.filter(o => o.isPriority || o.isEmergence);
    }

    // 3. City filter
    if (city && city !== 'TODAS') {
      resultOrders = resultOrders.filter(o => o.city === city);
    }

    // 4. Status filter
    if (status && status !== 'TODOS') {
      resultOrders = resultOrders.filter(o => o.statusName.toLowerCase() === status.toLowerCase());
    }

    setFilteredOrders(resultOrders);

    // Filter audit logs as well based on priority orders
    if (urgency !== 'TODOS') {
      const filteredNumbers = resultOrders.map(o => o.number);
      setFilteredLogs(reportsData.auditLogs.filter(log => filteredNumbers.includes(log.orderNumber)));
    } else {
      setFilteredLogs(reportsData.auditLogs);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  // Calculate stats based on filtered selection
  const totalFilteredCount = filteredOrders.length;
  const criticalItemsCount = filteredOrders.filter(o => o.isEmergence).length;
  const priorityItemsCount = filteredOrders.filter(o => o.isPriority).length;
  
  const totalProducedItems = filteredOrders.reduce((acc, current) => {
    const finishedCount = current.itemsList.filter(i => i.isProduced).length;
    return acc + finishedCount;
  }, 0);

  const totalItemsCount = filteredOrders.reduce((acc, current) => acc + current.itemsCount, 0);
  const averageCompletionRate = totalItemsCount > 0 ? Math.round((totalProducedItems / totalItemsCount) * 100) : 100;

  // Extract unique cities representing database indexes
  const distinctCities = reportsData 
    ? Array.from(new Set(reportsData.allOrders.map(o => o.city || 'Outra')))
    : ['Paraguaçu Paulista', 'Presidente Prudente', 'Ourinhos', 'Bauru', 'Marília'];

  // Current global reports layout rule configured by administrators
  const curConfig: ReportConfigData = reportsData?.config || {
    companyName: 'Master Coifa Ltda • Soluções em Aço Inox',
    layoutType: 'CLASSIFICACAO_CRITICA',
    footerText: 'Relatório operacional sigiloso para controle de fluxo físico.',
    includeAuditLog: true
  };

  if (isLoading) {
    return (
      <div id="reports-loading" className="d-flex flex-column align-items-center justify-content-center py-5 min-vh-50 font-sans">
        <div className="spinner-border text-primary mb-3" role="status">
          <span className="visually-hidden">Buscando banco de rotinas BI...</span>
        </div>
        <p className="text-secondary text-xs font-mono">Processando transações do MySQL...</p>
      </div>
    );
  }

  return (
    <div className="container-fluid py-1 print-container-layout font-sans text-dark" id="reports-module-root">
      
      {/* Inject professional print-focused stylesheet style for real-world paper PDF rendering */}
      <style>{`
        @media print {
          body {
            background-color: #ffffff !important;
            color: #000000 !important;
            font-size: 11px !important;
          }
          /* Hide non-printable system controls and decoration */
          #app-header, 
          #app-sidebar, 
          .navigation-sidebar-container, 
          .header-actions-area,
          #reports-control-card,
          .btn,
          #reports-summary-cards,
          .border-bottom-print-hide,
          .footer-actions-area,
          .alert {
            display: none !important;
          }
          /* Format printed paper size */
          .print-sheet-paper-page {
            border: none !important;
            padding: 0 !important;
            margin: 0 !important;
            box-shadow: none !important;
            background: transparent !important;
          }
          .table {
            border-color: #333 !important;
          }
          .table th {
            background-color: #f0f0f0 !important;
            color: #000000 !important;
            border-bottom: 2px solid #000 !important;
          }
          .badge {
            border: 1px solid #777 !important;
            color: #000000 !important;
            background: transparent !important;
          }
          .text-danger {
            color: #000000 !important;
            font-weight: bold !important;
          }
          .text-warning {
            color: #000000 !important;
          }
          .print-header-visible {
            display: block !important;
          }
        }
        .print-header-visible {
          display: none;
        }
      `}</style>

      {/* Screen Title & Action Panel */}
      <div className="d-flex align-items-center justify-content-between mb-4 border-bottom pb-3 border-bottom-print-hide">
        <div>
          <h1 className="h3 fw-black text-dark font-display mb-1 text-lowercase" style={{ letterSpacing: '-0.7px' }}>
            relatórios de produtividade
          </h1>
          <p className="text-muted text-xs font-mono mb-0">Roteamento de itens, estatísticas de ciclo e auditoria física para impressão</p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <button 
            onClick={handlePrint}
            className="btn btn-primary d-flex align-items-center gap-1.5 font-sans btn-sm px-4 py-2 text-white fw-bold shadow-xs rounded-3"
            style={{ backgroundColor: '#2f66aa', borderColor: '#2f66aa' }}
            type="button"
          >
            <Printer size={15} />
            Imprimir Relatório (PDF)
          </button>
        </div>
      </div>

      {/* Interactive Operational Filters Card (Rule 1 Compliant) */}
      <div className="card shadow-sm border border-secondary border-opacity-10 bg-white p-4 mb-4 border-bottom-print-hide" id="reports-control-card">
        <form onSubmit={handleFilterSubmit}>
          <div className="d-flex align-items-center gap-2 mb-3">
            <Filter size={16} className="text-primary" />
            <h6 className="fw-bold font-sans text-dark m-0">Filtros de Personalização do Lote</h6>
          </div>
          
          <div className="row g-3">
            <div className="col-12 col-md-3">
              <label className="form-label text-xxs font-mono text-muted uppercase tracking-wider mb-1">Período - De (Saída)</label>
              <input 
                type="date" 
                name="startDate"
                className="form-control text-xs font-sans py-2 rounded border border-light-subtle bg-white text-dark"
              />
            </div>
            <div className="col-12 col-md-3">
              <label className="form-label text-xxs font-mono text-muted uppercase tracking-wider mb-1">Período - Até (Saída)</label>
              <input 
                type="date" 
                name="endDate"
                className="form-control text-xs font-sans py-2 rounded border border-light-subtle bg-white text-dark"
              />
            </div>
            <div className="col-12 col-md-2">
              <label className="form-label text-xxs font-mono text-muted uppercase tracking-wider mb-1">Criticidade</label>
              <select name="urgency" className="form-select text-xs font-sans py-2 rounded border border-light-subtle bg-white text-dark">
                <option value="TODOS">Todos os Pedidos</option>
                <option value="CRITICAL_GROUP">Urgente + Emergência</option>
                <option value="PRIORITY_ONLY">Apenas Prioridades</option>
                <option value="EMERGENCE_ONLY">Apenas Emergências</option>
              </select>
            </div>
            <div className="col-12 col-md-2">
              <label className="form-label text-xxs font-mono text-muted uppercase tracking-wider mb-1">Módulo Cidade</label>
              <select name="city" className="form-select text-xs font-sans py-2 rounded border border-light-subtle bg-white text-dark">
                <option value="TODAS">Todas as Cidades</option>
                {distinctCities.map(c => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div className="col-12 col-md-2">
              <label className="form-label text-xxs font-mono text-muted uppercase tracking-wider mb-1">Status Geral</label>
              <select name="status" className="form-select text-xs font-sans py-2 rounded border border-light-subtle bg-white text-dark">
                <option value="TODOS">Todos</option>
                <option value="Aguardando">Aguardando</option>
                <option value="Produção">Em Produção</option>
                <option value="Coleta/Transporte">Transportadora Coleta</option>
                <option value="Entrega">Entrega Efetuada</option>
              </select>
            </div>
          </div>
          
          <div className="mt-3 pt-3 border-top d-flex justify-content-end">
            <button 
              type="submit" 
              className="btn btn-secondary px-4 py-2 font-sans text-xs text-white fw-bold rounded-2 border-0"
              style={{ backgroundColor: '#5c636a' }}
            >
              Aplicar Segmentação Analítica
            </button>
          </div>
        </form>
      </div>

      {/* Screen Dashboards Statistics Indicators */}
      <div className="row g-3 mb-4 border-bottom-print-hide" id="reports-summary-cards">
        <div className="col-12 col-md-4 col-lg-3">
          <div className="card shadow-sm border-0 bg-white p-3 h-100 d-flex flex-row align-items-center gap-3">
            <div className="bg-primary bg-opacity-10 p-2.5 rounded-3 text-primary">
              <Layers size={18} />
            </div>
            <div>
              <span className="text-xxs font-mono text-muted d-block uppercase">Pedidos Filtrados</span>
              <span className="h4 fw-black font-display text-dark mb-0">{totalFilteredCount} <span className="text-xs text-muted font-normal">unidades</span></span>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-4 col-lg-3">
          <div className="card shadow-sm border-0 bg-white p-3 h-100 d-flex flex-row align-items-center gap-3">
            <div className="bg-danger bg-opacity-10 p-2.5 rounded-3 text-danger">
              <AlertTriangle size={18} />
            </div>
            <div>
              <span className="text-xxs font-mono text-muted d-block uppercase">Foco Crítico</span>
              <span className="h4 fw-black font-display text-dark mb-0">{criticalItemsCount + priorityItemsCount} <span className="text-xs text-muted font-normal">atenciosos</span></span>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-4 col-lg-3">
          <div className="card shadow-sm border-0 bg-white p-3 h-100 d-flex flex-row align-items-center gap-3">
            <div className="bg-success bg-opacity-10 p-2.5 rounded-3 text-success">
              <CheckCircle size={18} />
            </div>
            <div>
              <span className="text-xxs font-mono text-muted d-block uppercase">Avanço Produzido</span>
              <span className="h4 fw-black font-display text-dark mb-0">{totalProducedItems} / {totalItemsCount}</span>
            </div>
          </div>
        </div>

        <div className="col-12 col-md-12 col-lg-3">
          <div className="card shadow-sm border-0 bg-white p-3 h-100 d-flex flex-row align-items-center gap-3">
            <div className="bg-info bg-opacity-10 p-2.5 rounded-3 text-info">
              <Activity size={18} />
            </div>
            <div className="flex-grow-1">
              <span className="text-xxs font-mono text-muted d-block uppercase">Aproveitamento Total</span>
              <div className="d-flex align-items-center justify-content-between">
                <span className="h4 fw-black font-display text-dark mb-0">{averageCompletionRate}%</span>
                <span className="text-xxs text-secondary font-mono">meta diária</span>
              </div>
              <div className="progress mt-1.5" style={{ height: '5px' }}>
                <div className="progress-bar bg-info" role="progressbar" style={{ width: `${averageCompletionRate}%` }} aria-valuenow={averageCompletionRate} aria-valuemin={0} aria-valuemax={100}></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Physical printable A4 model layout */}
      <div className="card shadow-md border border-secondary border-opacity-10 bg-white p-4 shadow-sm print-sheet-paper-page text-dark">
        
        {/* Printable pure header visible on papers */}
        <div className="print-header-visible border-bottom border-dark border-3 pb-3 mb-4 font-sans">
          <div className="d-flex justify-content-between align-items-start text-dark uppercase fw-black" style={{ fontSize: '1.2rem', letterSpacing: '-0.5px' }}>
            <span>{curConfig.companyName}</span>
            <span>RELATÓRIO DE EXPEDIÇÃO</span>
          </div>
          <div className="row g-2 font-mono text-xxs text-muted pt-2">
            <div className="col-4"><strong>SISTEMA:</strong> MASTER COIFA v1.0</div>
            <div className="col-4 text-center"><strong>EMISSÃO:</strong> {new Date().toLocaleString('pt-BR')}</div>
            <div className="col-4 text-end"><strong>PRESET:</strong> {curConfig.layoutType.replace(/_/g, ' ')}</div>
          </div>
          <div className="bg-light p-2 mt-2 border rounded-1 font-mono text-xxs text-secondary">
            <strong>Segmentação:</strong> Período: {appliedFilters.startDate || 'Sem início'} até {appliedFilters.endDate || 'Sem fim'} | 
            Criticidade: {appliedFilters.urgency} | Destino: {appliedFilters.city} | Status: {appliedFilters.status}
          </div>
        </div>

        {/* Dynamic header showing setting on screen as well */}
        <div className="d-flex justify-content-between align-items-center mb-4 border-bottom border-light pb-2 border-bottom-print-hide">
          <div className="d-flex align-items-center gap-2">
            <div className="p-1 px-2.5 rounded bg-dark bg-opacity-5 text-dark font-mono text-xxs fw-black">A4 LAYOUT ACTIVE</div>
            <span className="text-xs text-muted font-mono uppercase">Modelo Atual: {curConfig.layoutType}</span>
          </div>
          <span className="text-xxs text-muted font-mono">Gerado dinamicamente via Express</span>
        </div>

        <div className="text-dark bg-white font-sans">
          <h4 className="fw-black text-dark mb-0 font-display text-uppercase" style={{ fontSize: '1.15rem', letterSpacing: '-0.3px' }}>
            {curConfig.companyName}
          </h4>
          <p className="text-xs text-muted font-mono uppercase mb-4 border-bottom pb-2">FICHA TÉCNICA DE PRODUÇÃO - LISTAGEM ANALÍTICA DE PEDIDOS</p>

          <div className="table-responsive">
            <table className="table table-bordered align-middle text-dark font-sans" style={{ fontSize: '0.8rem' }}>
              <thead className="table-light font-mono text-xxs uppercase text-dark border-dark">
                <tr>
                  <th style={{ width: '80px' }}>Pedido</th>
                  <th>Cliente / Comprador</th>
                  <th style={{ width: '130px' }}>Cidade Destino</th>
                  <th style={{ width: '130px' }}>Saída Programada</th>
                  <th style={{ width: '150px' }}>Status Operação</th>
                  <th style={{ width: '90px' }}>Peças / Itens</th>
                </tr>
              </thead>
              <tbody>
                {filteredOrders.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="text-center py-4 text-muted font-mono text-xs">
                      Nenhum pedido compatível com as especificações do filtro operational.
                    </td>
                  </tr>
                ) : (
                  filteredOrders.map(order => {
                    const isEmerg = order.isEmergence;
                    const isPri = order.isPriority;
                    
                    // Layout customization rules (CLASSIFICACAO_CRITICA highlight)
                    const hasCritHighlight = curConfig.layoutType === 'CLASSIFICACAO_CRITICA' && (isEmerg || isPri);

                    return (
                      <React.Fragment key={order.id}>
                        <tr className={`${hasCritHighlight ? 'table-warning border-start border-danger border-4' : ''}`}>
                          <td className="font-mono text-center fw-bold text-dark">
                            #{order.number}
                          </td>
                          <td>
                            <div className="fw-bold text-dark">{order.customerName}</div>
                            {isEmerg && (
                              <span className="badge bg-danger text-white font-mono text-xxs uppercase py-0.5 px-1.5 fw-bold mt-1 inline-flex align-items-center gap-1">
                                <AlertTriangle size={10} /> Emergência Produtiva
                              </span>
                            )}
                            {!isEmerg && isPri && (
                              <span className="badge bg-warning text-dark font-mono text-xxs uppercase py-0.5 px-1.5 fw-bold mt-1">
                                Prioritário
                              </span>
                            )}
                          </td>
                          <td className="font-sans text-secondary">
                            <span className="d-inline-flex align-items-center gap-1">
                              <MapPin size={11} className="text-muted d-print-none" /> {order.city}
                            </span>
                          </td>
                          <td className="font-mono text-secondary">
                            <span className="d-inline-flex align-items-center gap-1">
                              <Calendar size={11} className="text-muted d-print-none" /> {order.departure}
                            </span>
                          </td>
                          <td>
                            {/* Color status mappings matching floor criteria */}
                            <span className="badge border border-secondary text-secondary fw-semibold font-mono text-xxs px-2 py-1 uppercase text-dark">
                              {order.statusName}
                            </span>
                          </td>
                          <td className="font-mono text-center">
                            {order.itemsCount} {order.itemsCount === 1 ? 'Peça' : 'Peças'}
                          </td>
                        </tr>

                        {/* Display underlying children items depending on detailed auditing presets */}
                        {(curConfig.layoutType === 'DETALHADO_AUDITORIA' || curConfig.layoutType === 'CLASSIFICACAO_CRITICA') && order.itemsList && order.itemsList.length > 0 && (
                          <tr className="table-light border-light-subtle">
                            <td colSpan={6} className="p-0">
                              <div className="px-4 py-2 border-bottom border-light">
                                <span className="text-xxs font-mono text-uppercase text-secondary fw-bold d-block mb-1">Mapeamento Unitário do Pedido #{order.number}:</span>
                                <div className="row g-2">
                                  {order.itemsList.map((item, idx) => (
                                    <div key={item.id || item.productId || idx} className="col-12 col-md-4">
                                      <div className="bg-white p-2 rounded border border-light-subtle text-xxs d-flex align-items-center justify-content-between">
                                        <span className="fw-semibold text-dark truncate-text">{item.productName}</span>
                                        <span className={`badge font-mono text-xxxs ${item.isProduced ? 'bg-success text-white' : 'bg-secondary text-white'}`}>
                                          {item.status}
                                        </span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Section: Operator Production Audit Logs (Conditional based on Settings layout Configs) */}
          {curConfig.includeAuditLog && filteredLogs.length > 0 && (
            <div className="mt-4 pt-3 border-top border-secondary border-opacity-10 font-sans text-dark">
              <h6 className="fw-bold font-sans text-dark text-uppercase mb-2 font-mono" style={{ fontSize: '0.78rem' }}>
                Log Cronológico de Auditoria do Lote (Sinalização Chão de Fábrica)
              </h6>
              <p className="text-xxs text-muted mb-3 font-sans border-bottom pb-1 border-bottom-print-inline">As coletas a seguir foram registradas de forma assíncrona por leitores e operadores operantes da unidade.</p>
              
              <div className="row g-2">
                {filteredLogs.map(log => (
                  <div key={log.id} className="col-12 col-md-6 col-lg-4">
                    <div className="bg-light p-2.5 rounded border border-secondary border-opacity-10 text-xxs">
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <span className="fw-bold text-dark font-mono">Ped #{log.orderNumber}</span>
                        <span className="text-muted font-mono text-xxxs">{log.date}</span>
                      </div>
                      <p className="mb-1 text-secondary text-xxs font-sans">
                        <span className="fw-bold text-primary font-mono lowercase">[{log.sector}]</span> - {log.action}
                      </p>
                      <div className="d-flex justify-content-between text-xxs text-muted pt-1 border-top border-dashed font-mono" style={{ fontSize: '0.62rem' }}>
                        <span>Duração Ativa:</span>
                        <span className="fw-bold text-dark">{log.durationMinutes} minutos</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Legal and compliance floor notes */}
          <div className="border-top border-dark border-1 pt-3 mt-4 text-dark font-sans">
            <div className="row g-3">
              <div className="col-12 col-md-7">
                <p className="text-xxs text-secondary leading-normal font-mono mb-0" style={{ maxWidth: '480px' }}>
                  {curConfig.footerText || 'Relatório operacional sigiloso para controle de fluxo físico.'}
                </p>
              </div>
              <div className="col-12 col-md-5">
                <div className="row g-3 text-center text-xxs font-mono pt-2">
                  <div className="col-6">
                    <div className="border-top pt-1.5 text-muted">Visto Logística</div>
                  </div>
                  <div className="col-6">
                    <div className="border-top pt-1.5 text-muted">Carimbo Administrativo</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>

      </div>

    </div>
  );
}
